// src/pages/Credits.tsx
//
// User-facing credits dashboard. Shows the current balance, the credit
// packs, active usage clocks (apps/games the user has already paid for
// this period), and a recent transaction history.

import React, { useEffect, useMemo, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import {
  fetchCreditBalance,
  fetchCreditPackages,
  purchaseCredits,
  confirmCheckoutSession,
  fetchPurchaseStatus,
  fetchCreditHistory,
  fetchActiveClocks,
  validatePromo,
  fetchSubscriptionTiers,
  fetchMySubscription,
  subscribe,
  type CreditBundle,
  type CreditTransaction,
  type ActiveClock,
  type PromoValidation,
  type SubscriptionTier,
  type UserSubscription,
} from '../utils/creditsApi';

const fmtUsd = (n: number) => `$${n.toFixed(2)}`;

const PERIOD_LABEL: Record<'day' | 'week' | 'month' | 'year', string> = {
  day: 'per day',
  week: 'per week',
  month: 'per month',
  year: 'per year',
};

const KIND_LABELS: Record<CreditTransaction['kind'], string> = {
  purchase: 'Purchased',
  usage: 'Used',
  grant: 'Granted',
  refund: 'Refunded',
  adjustment: 'Adjustment',
};

/** Pretty-print an ISO string in local time, short date + time. */
const fmtDate = (iso: string) => {
  try {
    return new Date(iso).toLocaleString(undefined, {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  } catch {
    return iso;
  }
};

/** "Resets in 3d 5h" — relative time until the expires_at timestamp. */
const relativeRemaining = (iso: string): string => {
  const diff = new Date(iso).getTime() - Date.now();
  if (diff <= 0) return 'expired';
  const hours = Math.floor(diff / (1000 * 60 * 60));
  if (hours >= 24) {
    const days = Math.floor(hours / 24);
    const leftoverHours = hours % 24;
    return `${days}d ${leftoverHours}h`;
  }
  if (hours >= 1) {
    return `${hours}h`;
  }
  const minutes = Math.max(1, Math.floor(diff / (1000 * 60)));
  return `${minutes}m`;
};

const Credits: React.FC = () => {
  const { token, isAuthenticated } = useAuth();
  const [balance, setBalance] = useState<number>(0);
  const [packages, setPackages] = useState<CreditBundle[]>([]);
  const [history, setHistory] = useState<CreditTransaction[]>([]);
  const [clocks, setClocks] = useState<ActiveClock[]>([]);
  const [loading, setLoading] = useState(true);
  const [purchasingId, setPurchasingId] = useState<number | null>(null);
  const [toast, setToast] = useState<string | null>(null);
  const [purchaseAvailable, setPurchaseAvailable] = useState(true);

  // Promo code — the input feeds the bundle grid. Applied promo is the
  // validated response we got from /validate-promo, so the UI can show
  // the discounted total.
  const [promoCode, setPromoCode] = useState('');
  const [promoBundleId, setPromoBundleId] = useState<number | ''>('');
  const [applyingPromo, setApplyingPromo] = useState(false);
  const [appliedPromo, setAppliedPromo] = useState<PromoValidation | null>(null);
  const [promoError, setPromoError] = useState<string | null>(null);

  // Subscription tiers
  const [tiers, setTiers] = useState<SubscriptionTier[]>([]);
  const [mySub, setMySub] = useState<UserSubscription | null>(null);
  const [subscribing, setSubscribing] = useState<string | null>(null);

  const reload = async () => {
    if (!token) return;
    const [bal, hist, cks, sub] = await Promise.all([
      fetchCreditBalance(token),
      fetchCreditHistory(token),
      fetchActiveClocks(token),
      fetchMySubscription(token),
    ]);
    setBalance(bal);
    setHistory(hist);
    setClocks(cks);
    setMySub(sub);
  };

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const [pkgResp, tierList, status] = await Promise.all([
          fetchCreditPackages(),
          fetchSubscriptionTiers(token),
          fetchPurchaseStatus(),
        ]);
        setPackages(pkgResp.packages);
        setTiers(tierList);
        setPurchaseAvailable(status.available);
        if (token) await reload();
      } finally {
        setLoading(false);
      }
    };
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  // Returning from Stripe Checkout — confirm the session (grants immediately if
  // the webhook is still in flight), show a receipt toast, refresh, clean the URL.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const purchased = params.get('purchased');
    const subscribed = params.get('subscribed');
    const sessionId = params.get('session_id');
    if (!purchased && !subscribed) return;
    (async () => {
      if (token && sessionId) {
        const r = await confirmCheckoutSession(token, sessionId);
        if (r && typeof r.balance === 'number') setBalance(r.balance);
      }
      setToast(
        subscribed
          ? `You're subscribed — your ${subscribed} plan credits are on the way.`
          : 'Payment received — credits added to your account!',
      );
      if (token) await reload();
      window.history.replaceState({}, '', '/credits');
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  // Returns the promo code string iff the applied promo covers the
  // given bundle (or any bundle for the "All bundles" case).
  const promoCodeForBundle = (bundle: CreditBundle): string | undefined => {
    if (!appliedPromo) return undefined;
    const scoped = appliedPromo.promo.bundleId;
    if (scoped != null && scoped !== bundle.id) return undefined;
    return appliedPromo.promo.code;
  };

  const handlePurchasePackage = async (pkg: CreditBundle) => {
    if (!token) return;
    setPurchasingId(pkg.id);
    setToast(null);
    const result = await purchaseCredits(token, {
      bundleId: pkg.id,
      promoCode: promoCodeForBundle(pkg),
    });
    if (result?.url) {
      window.location.href = result.url; // off to Stripe Checkout
      return;
    }
    if (result?.free) {
      if (typeof result.balance === 'number') setBalance(result.balance);
      setToast(result.message || `Added ${result.credited} credits.`);
      setAppliedPromo(null);
      setPromoCode('');
      await reload();
    } else {
      setToast('Purchase failed. Please try again.');
    }
    setPurchasingId(null);
  };

  // Promo code validation — user types a code, picks a bundle (or "any
  // bundle"), and we call /validate-promo to get the dollar discount.
  // The result is stored on `appliedPromo` so the bundle grid can show
  // the discounted total on the matching card.
  const handleApplyPromo = async () => {
    if (!token) return;
    const trimmed = promoCode.trim();
    if (!trimmed) {
      setPromoError('Enter a promo code');
      return;
    }
    setApplyingPromo(true);
    setPromoError(null);
    const result = await validatePromo(token, {
      code: trimmed,
      bundleId: promoBundleId === '' ? null : Number(promoBundleId),
    });
    if ('data' in result) {
      setAppliedPromo(result.data);
      setPromoError(null);
    } else {
      setAppliedPromo(null);
      setPromoError(result.error);
    }
    setApplyingPromo(false);
  };

  const clearPromo = () => {
    setAppliedPromo(null);
    setPromoError(null);
    setPromoCode('');
    setPromoBundleId('');
  };

  const handleSubscribe = async (tier: string) => {
    if (!token) return;
    setSubscribing(tier);
    const result = await subscribe(token, tier);
    if (result?.url) {
      window.location.href = result.url; // off to Stripe Checkout
      return;
    }
    if (result?.free || result?.success) {
      setToast(result.message || `Subscribed to ${tier}!`);
      await reload();
    } else {
      setToast('Subscription failed. Please try again.');
    }
    setSubscribing(null);
  };

  const packagesSorted = useMemo(() => [...packages].sort((a, b) => a.credits - b.credits), [packages]);

  if (loading) {
    return (
      <div className="section container" style={{ textAlign: 'center', padding: '4rem' }}>
        <p>Loading credits...</p>
      </div>
    );
  }

  return (
    <div>
      {!purchaseAvailable && (
        <div
          style={{
            backgroundColor: '#fef3c7',
            color: '#92400e',
            borderBottom: '1px solid #fde68a',
            padding: '0.85rem 1rem',
            textAlign: 'center',
            fontWeight: 500,
          }}
        >
          Purchasing is not available at this time — please come back later.
        </div>
      )}
      <section className="section bg-light">
        <div className="container">
          <h1 style={{ marginBottom: '0.5rem' }}>Credits</h1>
          <p className="text-secondary text-large">
            Credits fuel every Snowbird Industries app and game. Pick a credit pack below.
          </p>
        </div>
      </section>

      {isAuthenticated && (
        <section className="section">
          <div className="container">
            <div className="card" style={{ padding: '1.75rem', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '1rem' }}>
              <div>
                <p className="text-secondary" style={{ margin: 0, fontSize: '0.875rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Current Balance</p>
                <p style={{ margin: '0.25rem 0 0', fontSize: '2.5rem', fontWeight: 700 }}>
                  {balance.toFixed(2)} <span style={{ fontSize: '1rem', color: '#6b7280', fontWeight: 500 }}>credits</span>
                </p>
              </div>
              {toast && (
                <div style={{ maxWidth: '320px', fontSize: '0.875rem', color: '#166534', backgroundColor: '#dcfce7', border: '1px solid #86efac', padding: '0.75rem 1rem', borderRadius: '8px' }}>
                  {toast}
                </div>
              )}
            </div>
          </div>
        </section>
      )}

      {/* Subscription tiers */}
      <section className="section">
        <div className="container">
          <h2>Subscription Plans</h2>
          <p className="text-secondary" style={{ marginBottom: '1.5rem' }}>
            Choose a plan that fits how you use Snowbird Industries. Credits refill monthly — upgrade or downgrade anytime.
          </p>

          {/* Current subscription badge */}
          {mySub && (
            <div style={{ marginBottom: '1.5rem', padding: '1rem 1.5rem', backgroundColor: '#eef2ff', borderRadius: '8px', border: '1px solid #c7d2fe' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '0.75rem' }}>
                <div>
                  <span style={{ fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.05em', color: '#6366f1' }}>Current Plan</span>
                  <h3 style={{ margin: '0.15rem 0 0', fontSize: '1.2rem' }}>{mySub.name}</h3>
                  <p style={{ margin: '0.15rem 0 0', fontSize: '0.85rem', color: '#6b7280' }}>
                    {mySub.monthlyCredits.toLocaleString()} credits/month
                    {mySub.pricePerMonth > 0 ? ` — ${fmtUsd(mySub.pricePerMonth)}/mo` : ' — Free'}
                  </p>
                </div>
                {mySub.nextRefillAt && (
                  <div style={{ textAlign: 'right' }}>
                    <p style={{ fontSize: '0.75rem', color: '#6b7280', margin: 0 }}>Next refill</p>
                    <p style={{ fontSize: '0.85rem', fontWeight: 600, margin: '0.1rem 0 0' }}>
                      {new Date(mySub.nextRefillAt).toLocaleDateString()}
                    </p>
                  </div>
                )}
              </div>
            </div>
          )}

          <div className="grid grid-4" style={{ gap: '1rem' }}>
            {tiers.map((tier) => {
              const isCurrent = mySub?.tier === tier.tier;
              const isPopular = tier.tier === 'standard';
              return (
                <div
                  key={tier.tier}
                  className="card"
                  style={{
                    padding: '1.5rem', display: 'flex', flexDirection: 'column', gap: '0.75rem',
                    border: isPopular ? '2px solid #4f46e5' : isCurrent ? '2px solid #16a34a' : '1px solid #e5e7eb',
                    position: 'relative',
                  }}
                >
                  {isPopular && (
                    <span style={{
                      position: 'absolute', top: '-10px', left: '50%', transform: 'translateX(-50%)',
                      backgroundColor: '#4f46e5', color: '#fff', padding: '0.15rem 0.75rem',
                      borderRadius: '9999px', fontSize: '0.7rem', fontWeight: 600,
                    }}>
                      MOST POPULAR
                    </span>
                  )}
                  {isCurrent && (
                    <span style={{
                      position: 'absolute', top: '-10px', right: '12px',
                      backgroundColor: '#16a34a', color: '#fff', padding: '0.15rem 0.6rem',
                      borderRadius: '9999px', fontSize: '0.65rem', fontWeight: 600,
                    }}>
                      CURRENT
                    </span>
                  )}
                  <h3 style={{ margin: 0 }}>{tier.name}</h3>
                  <div>
                    <p style={{ margin: 0, fontSize: '1.75rem', fontWeight: 700 }}>
                      {tier.pricePerMonth > 0 ? fmtUsd(tier.pricePerMonth) : 'Free'}
                      <span style={{ fontSize: '0.85rem', fontWeight: 400, color: '#6b7280' }}>/mo</span>
                    </p>
                    <p style={{ margin: '0.15rem 0 0', fontSize: '0.85rem', color: '#6b7280' }}>
                      {tier.monthlyCredits.toLocaleString()} credits/month
                    </p>
                  </div>
                  <p className="text-secondary" style={{ margin: 0, fontSize: '0.82rem' }}>{tier.description}</p>
                  <ul style={{ margin: 0, padding: '0 0 0 1.1rem', fontSize: '0.8rem', color: '#374151', flex: 1 }}>
                    {tier.features.map((f, i) => (
                      <li key={i} style={{ marginBottom: '0.25rem' }}>{f}</li>
                    ))}
                  </ul>
                  <button
                    className={isPopular ? 'btn btn-primary' : 'btn btn-secondary'}
                    style={{ marginTop: 'auto' }}
                    onClick={() => handleSubscribe(tier.tier)}
                    disabled={!isAuthenticated || isCurrent || subscribing === tier.tier || !purchaseAvailable}
                  >
                    {subscribing === tier.tier ? 'Processing…'
                      : isCurrent ? 'Current Plan'
                      : isAuthenticated ? `Choose ${tier.name}`
                      : 'Sign in'}
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* One-time credit top-ups */}
      <section className="section">
        <div className="container">
          <h2>One-Time Top-Ups</h2>
          <p className="text-secondary" style={{ marginBottom: '1.5rem' }}>
            Choose the bundle that fits how you use Snowbird Industries.
          </p>

          {/* Promo code panel — collects a code + target bundle and
              stores the validated response so the matching bundle card
              below can show the discounted total. */}
          {isAuthenticated && (
            <div className="card" style={{ padding: '1rem 1.25rem', marginBottom: '1.5rem', display: 'flex', flexWrap: 'wrap', gap: '0.75rem', alignItems: 'flex-end' }}>
              <div style={{ flex: '1 1 180px' }}>
                <label style={{ display: 'block', marginBottom: '0.4rem', fontSize: '0.85rem', fontWeight: 500 }}>
                  Promo Code
                </label>
                <input
                  type="text"
                  value={promoCode}
                  onChange={(e) => { setPromoCode(e.target.value); setAppliedPromo(null); setPromoError(null); }}
                  placeholder="e.g. WELCOME10"
                  style={{ width: '100%', padding: '0.55rem 0.75rem', borderRadius: '6px', border: '1px solid #d1d5db', textTransform: 'uppercase' }}
                />
              </div>
              <div style={{ flex: '0 1 200px' }}>
                <label style={{ display: 'block', marginBottom: '0.4rem', fontSize: '0.85rem', fontWeight: 500 }}>
                  Apply to
                </label>
                <select
                  value={promoBundleId}
                  onChange={(e) => { setPromoBundleId(e.target.value === '' ? '' : Number(e.target.value)); setAppliedPromo(null); }}
                  style={{ width: '100%', padding: '0.55rem 0.75rem', borderRadius: '6px', border: '1px solid #d1d5db' }}
                >
                  <option value="">Any bundle</option>
                  {packagesSorted.map((p) => (
                    <option key={p.id} value={p.id}>{p.name}</option>
                  ))}
                </select>
              </div>
              <button
                type="button"
                className="btn btn-secondary"
                onClick={handleApplyPromo}
                disabled={applyingPromo || !promoCode.trim()}
              >
                {applyingPromo ? 'Checking…' : 'Apply'}
              </button>
              {appliedPromo && (
                <div style={{ fontSize: '0.85rem', color: '#166534', backgroundColor: '#dcfce7', padding: '0.45rem 0.75rem', borderRadius: '6px' }}>
                  <strong>{appliedPromo.promo.code}</strong> — {fmtUsd(appliedPromo.discount)} off{' '}
                  <button
                    type="button"
                    onClick={clearPromo}
                    style={{ marginLeft: '0.5rem', background: 'none', border: 'none', color: '#166534', textDecoration: 'underline', cursor: 'pointer', fontSize: 'inherit' }}
                  >
                    remove
                  </button>
                </div>
              )}
              {promoError && (
                <div style={{ fontSize: '0.85rem', color: '#dc2626' }}>{promoError}</div>
              )}
            </div>
          )}

          <div className="grid grid-4" style={{ gap: '1.25rem' }}>
            {packagesSorted.map((pkg, idx) => {
              // Highlight the largest bundle (last in the sorted list) as
              // the "best value" so there's still a marketing nudge
              // without revealing absolute cost-per-credit math.
              const isBestValue = idx === packagesSorted.length - 1 && packagesSorted.length > 1;
              const bundleTotal = pkg.total ?? 0;
              const promoMatchesThisBundle =
                appliedPromo != null &&
                (appliedPromo.promo.bundleId == null || appliedPromo.promo.bundleId === pkg.id);
              const promoDiscount = promoMatchesThisBundle ? appliedPromo!.discount : 0;
              const finalTotal = Math.max(0, Number((bundleTotal - promoDiscount).toFixed(2)));
              return (
                <div key={pkg.id} className="card" style={{ padding: '1.5rem', display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                  <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between' }}>
                    <h3 style={{ margin: 0 }}>{pkg.name}</h3>
                    {isBestValue && (
                      <span style={{
                        backgroundColor: '#dcfce7',
                        color: '#166534',
                        padding: '0.2rem 0.6rem',
                        borderRadius: '9999px',
                        fontSize: '0.7rem',
                        fontWeight: 600,
                      }}>
                        BEST VALUE
                      </span>
                    )}
                  </div>
                  <div>
                    <p style={{ margin: 0, fontSize: '1.75rem', fontWeight: 700 }}>{fmtUsd(finalTotal)}</p>
                    {/* Strike-through only applies to a user-entered promo.
                        We never expose the pre-bundle-discount subtotal
                        because dividing it by the credit count leaks the
                        base cost per credit. */}
                    {promoMatchesThisBundle && (
                      <p style={{ margin: '0.1rem 0 0', fontSize: '0.85rem', color: '#9ca3af', textDecoration: 'line-through' }}>
                        {fmtUsd(bundleTotal)}
                      </p>
                    )}
                  </div>
                  <p className="text-secondary" style={{ margin: 0, fontSize: '0.85rem' }}>
                    {pkg.credits} credits
                  </p>
                  {promoMatchesThisBundle && promoDiscount > 0 && (
                    <p style={{ margin: 0, fontSize: '0.8rem', color: '#166534', fontWeight: 500 }}>
                      Promo {appliedPromo!.promo.code}: −{fmtUsd(promoDiscount)}
                    </p>
                  )}
                  <button
                    className="btn btn-primary"
                    style={{ marginTop: 'auto' }}
                    onClick={() => handlePurchasePackage(pkg)}
                    disabled={!isAuthenticated || purchasingId === pkg.id || !purchaseAvailable}
                  >
                    {purchasingId === pkg.id
                      ? 'Processing…'
                      : !purchaseAvailable
                        ? 'Unavailable'
                        : isAuthenticated
                          ? 'Buy Now'
                          : 'Sign in to Buy'}
                  </button>
                </div>
              );
            })}
            {packagesSorted.length === 0 && (
              <div className="card" style={{ gridColumn: '1 / -1', padding: '1.5rem', textAlign: 'center', color: '#6b7280' }}>
                No bundles are configured yet. Admins can add them in Credits Management.
              </div>
            )}
          </div>
        </div>
      </section>

      {isAuthenticated && clocks.length > 0 && (
        <section className="section">
          <div className="container">
            <h2>Active Passes</h2>
            <p className="text-secondary" style={{ marginBottom: '1rem' }}>
              Apps and games you've already paid for this period. You can re-launch them for free until the timer resets.
            </p>
            <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ backgroundColor: '#f9fafb' }}>
                    <th style={{ padding: '0.85rem 1rem', textAlign: 'left', borderBottom: '1px solid #e5e7eb' }}>Target</th>
                    <th style={{ padding: '0.85rem 1rem', textAlign: 'left', borderBottom: '1px solid #e5e7eb' }}>Type</th>
                    <th style={{ padding: '0.85rem 1rem', textAlign: 'right', borderBottom: '1px solid #e5e7eb' }}>Credits</th>
                    <th style={{ padding: '0.85rem 1rem', textAlign: 'left', borderBottom: '1px solid #e5e7eb' }}>Resets In</th>
                  </tr>
                </thead>
                <tbody>
                  {clocks.map((c) => (
                    <tr key={c.id}>
                      <td style={{ padding: '0.85rem 1rem', borderBottom: '1px solid #f3f4f6' }}>
                        <strong>{c.target_name || c.target_id}</strong>
                      </td>
                      <td style={{ padding: '0.85rem 1rem', borderBottom: '1px solid #f3f4f6', textTransform: 'capitalize' }}>
                        {c.target_type}
                      </td>
                      <td style={{ padding: '0.85rem 1rem', borderBottom: '1px solid #f3f4f6', textAlign: 'right' }}>
                        {Number(c.credits_charged).toFixed(0)} {PERIOD_LABEL[c.period]}
                      </td>
                      <td style={{ padding: '0.85rem 1rem', borderBottom: '1px solid #f3f4f6' }}>
                        {relativeRemaining(c.expires_at)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </section>
      )}

      {isAuthenticated && history.length > 0 && (
        <section className="section">
          <div className="container">
            <h2>Recent Activity</h2>
            <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ backgroundColor: '#f9fafb' }}>
                    <th style={{ padding: '0.85rem 1rem', textAlign: 'left', borderBottom: '1px solid #e5e7eb' }}>When</th>
                    <th style={{ padding: '0.85rem 1rem', textAlign: 'left', borderBottom: '1px solid #e5e7eb' }}>Kind</th>
                    <th style={{ padding: '0.85rem 1rem', textAlign: 'left', borderBottom: '1px solid #e5e7eb' }}>Detail</th>
                    <th style={{ padding: '0.85rem 1rem', textAlign: 'right', borderBottom: '1px solid #e5e7eb' }}>Δ</th>
                    <th style={{ padding: '0.85rem 1rem', textAlign: 'right', borderBottom: '1px solid #e5e7eb' }}>Balance</th>
                  </tr>
                </thead>
                <tbody>
                  {history.slice(0, 25).map((tx) => (
                    <tr key={tx.id}>
                      <td style={{ padding: '0.85rem 1rem', borderBottom: '1px solid #f3f4f6', fontSize: '0.85rem', color: '#6b7280' }}>{fmtDate(tx.created_at)}</td>
                      <td style={{ padding: '0.85rem 1rem', borderBottom: '1px solid #f3f4f6' }}>{KIND_LABELS[tx.kind] || tx.kind}</td>
                      <td style={{ padding: '0.85rem 1rem', borderBottom: '1px solid #f3f4f6', fontSize: '0.85rem', color: '#6b7280' }}>
                        {tx.note || (tx.reference_id ? `${tx.reference_type || ''}: ${tx.reference_id}` : '—')}
                      </td>
                      <td style={{ padding: '0.85rem 1rem', borderBottom: '1px solid #f3f4f6', textAlign: 'right', fontWeight: 600, color: Number(tx.delta) >= 0 ? '#16a34a' : '#dc2626' }}>
                        {Number(tx.delta) > 0 ? '+' : ''}{Number(tx.delta).toFixed(2)}
                      </td>
                      <td style={{ padding: '0.85rem 1rem', borderBottom: '1px solid #f3f4f6', textAlign: 'right' }}>
                        {Number(tx.balance_after).toFixed(2)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </section>
      )}
    </div>
  );
};

export default Credits;
